import {
  ArrowLeftOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Descriptions,
  Divider,
  Skeleton,
  Space,
  Spin,
  Typography,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.entry";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

import { clearQuote } from "../../../store/quotes/quotesSlice";
import { fetchQuote } from "../../../store/quotes/quotesThunks";
import { clearQuotePdf } from "../../../store/renewalsSearch/renewalsSearchSlice";
import { fetchQuotePdf } from "../../../store/renewalsSearch/renewalsSearchThunks";
import ApplicationHeaderBar from "../../applications/shared/ApplicationHeaderBar";
import {
  QUOTE_STATUS_DISPLAY,
  QUOTE_STATUSES_MAP,
} from "../../../shared/constants";

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

const ORIG_PDF_WIDTH = 819;
const MIN_SCALE = 0.8;
const MAX_SCALE = 1.4;

export default function ArchivedQuoteView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { applicationId } = useParams();

  const data = useSelector((s) => s.quotes.current.data);
  const isLoading = useSelector((s) => s.quotes.current.isLoading);

  // PDF State
  const [scale, setScale] = useState(0.85);
  const pdfBoxRef = useRef(null);
  const isPdfLoading = useSelector(
    (state) => state.renewalsSearch.quotePdf.isLoading
  );
  const url = useSelector((state) => state.renewalsSearch.quotePdf.url);
  const pdfError = useSelector((state) => state.renewalsSearch.quotePdf.error);

  // Reset PDF data
  useEffect(() => {
    dispatch(clearQuotePdf());
    return () => {
      dispatch(clearQuotePdf());
      dispatch(clearQuote());
    };
  }, [dispatch]);

  //
  useEffect(() => {
    if (!applicationId) return;

    const fetchData = async () => {
      try {
        const res = await dispatch(fetchQuote({ id: applicationId })).unwrap();
        await dispatch(fetchQuotePdf({ id: res.id })).unwrap();
      } catch (error) {
        console.error("Error fetching quote before PDF:", error);
      }
    };

    fetchData();
  }, [dispatch, applicationId]);

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  const headerActions = useMemo(
    () => [
      <Button key="back" size="small" onClick={() => navigate(-1)}>
        <ArrowLeftOutlined /> Go Back To Renewal
      </Button>,
    ],
    [navigate]
  );

  const zoomIn = () =>
    setScale((s) => Math.min(MAX_SCALE, +(s + 0.1).toFixed(2)));
  const zoomOut = () =>
    setScale((s) => Math.max(MIN_SCALE, +(s - 0.1).toFixed(2)));

  // Simple aircraft list -> bullet points
  const aircraftItems = useMemo(() => {
    if (!data) return [];
    const primary = `${data.year ?? "-"} ${data.make ?? "-"} ${
      data.model ?? "-"
    } (${data.registration ?? "-"}), Passenger Seats: ${
      data.passenger_seats ?? "-"
    }`;
    const extra = Array.isArray(data.additional_aircraft)
      ? data.additional_aircraft
      : [];
    return [
      primary,
      ...extra.map(
        (a) =>
          `${a?.year ?? "-"} ${a?.make ?? "-"} ${a?.model ?? "-"} (${
            a?.registration ?? "-"
          }), Passenger Seats: ${a?.passenger_seats ?? "-"}`
      ),
    ];
  }, [data]);

  if (data == null && !isLoading) {
    return (
      <Alert
        message={`Error: Something went wrong while loading this quote!`}
        type="error"
        showIcon
        className="default-margin"
      />
    );
  }

  return (
    <Card className="body-card-content-no-min-height">
      <Skeleton active loading={isLoading}>
        {data && (
          <>
            <ApplicationHeaderBar data={data} actions={headerActions} />

            <Descriptions
              size="small"
              bordered
              column={{ xxl: 4, xl: 4, lg: 2, md: 1, sm: 1, xs: 1 }}
              labelStyle={{ width: 160 }}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Internal Comments" span={2}>
                <Typography.Paragraph
                  style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                >
                  {data.internal_comments?.trim() || "—"}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="Status" span={1}>
                <Badge
                  status={QUOTE_STATUSES_MAP[data.status] || ""}
                  text={QUOTE_STATUS_DISPLAY[data.status] || "—"}
                />
              </Descriptions.Item>

              <Descriptions.Item label="Customer Code" span={1}>
                {data.customer_code ?? "—"}
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <div style={{ minWidth: 210 }}>Underwriting Comments</div>
                }
                span={2}
              >
                <Typography.Paragraph
                  style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                >
                  {data.underwriting_comments?.trim() || "—"}
                </Typography.Paragraph>
              </Descriptions.Item>

              <Descriptions.Item label="List of Aircraft" span={2}>
                {aircraftItems.length ? (
                  <Typography.Paragraph
                    style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                  >
                    {aircraftItems.join("\n")}
                  </Typography.Paragraph>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: "12px 0 16px" }} />
            <div className="pdf-toolbar">
              <Space>
                <Button
                  size="small"
                  icon={<MinusOutlined />}
                  onClick={zoomOut}
                  disabled={scale === MIN_SCALE}
                />
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={zoomIn}
                  disabled={scale === MAX_SCALE}
                />
                <span className="page-indicator">
                  {Math.round(scale * 100)} %
                </span>
              </Space>
            </div>

            <Spin spinning={isPdfLoading}>
              <div ref={pdfBoxRef} className="pdf-box-archived-quote">
                <Document
                  file={url}
                  options={{ disableFontFace: true }}
                  noData={
                    isPdfLoading ? (
                      <div className="pdf-loading">
                        <Spin />
                      </div>
                    ) : (
                      <div style={{ textAlign: "center", padding: 40 }}>
                        No document found
                      </div>
                    )
                  }
                  error={
                    isPdfLoading ? (
                      <div className="pdf-loading"></div>
                    ) : (
                      <div style={{ color: "red" }}>
                        {pdfError ??
                          "Something went wrong while loading the PDF"}
                      </div>
                    )
                  }
                  loading={
                    <div className="pdf-loading">
                      <Spin />
                    </div>
                  }
                >
                  <div key="pdf_page_2" className="pdf-page-wrapper">
                    <Page
                      pageNumber={2}
                      width={ORIG_PDF_WIDTH * scale}
                      renderTextLayer={false}
                      loading={<div className="pdf-loading"></div>}
                    />
                  </div>
                </Document>
              </div>
            </Spin>
          </>
        )}
      </Skeleton>
    </Card>
  );
}
